import React, { useState, useEffect } from 'react';
import { Modal as ModalComponent } from 'components/modal/Modal';
import { Form, Formik, FormikHelpers } from 'formik';
import FormTextField from 'components/form/FormTextField';
import { WrappedFormSingleSelectField, SelectOption } from 'components/form/FormSingleSelectField';
import Button from 'components/button/Button';
import { Spinner } from 'components/spinner/Spinner';
import * as Yup from 'yup';
import instance, { ApiResponse } from 'api';
import { endpoints } from 'endpoints.config';
import { Toast } from 'helpers/toast';
import { useSelector } from 'react-redux';
import { selectComponentResources } from 'reducers/componentResources';
import { useDispatch } from 'react-redux';
import { closeModal } from 'reducers/modal';
import { getCustomerComponentResources } from 'reducers/componentResources';


type Props = {
    onClose: () => void;
    onSuccess: () => void;
};

type CryptoEnablementFormValues = {
    blockchainAddresses: string;
    industryType: string;
    digitalAssetsTrading: string;
    anticipatedMonthlyCryptoTransactionsId: number | null;
    anticipatedMonthlyCryptoVolumeUsdId: number | null;
    cryptoBusinessPlan: string;
    cryptoPaymentTypes: string;
    integrationMethodId: number | null;
    expectedMonthlyPaymentVolumeUsdId: number | null;
    averageTransactionSize: string;
    settlementPreferenceId: number | null;
};

type PersonalCryptoFormValues = {
    purchaseCryptoMonthlyVolume: string;
    purchaseCryptoAverageAmount: string;
    purchaseCryptoTotalAmountPerMonth: string;
    sellingCryptoAverageAmount: string;
    sellingCryptoMonthlyVolume: string;
    sellingCryptoTotalAmountPerMonth: string;
    totalMonthlyVolume: string;
    totalAverageAmount: string;
    totalMonthlyAmount: string;
    btcTradingPercentage: string;
    ethTradingPercentage: string;
    usdcTradingPercentage: string;
    otherCoinsTradingPercentage: string;
};

const businessValidationSchema = Yup.object({
    blockchainAddresses: Yup.string().required('Blockchain addresses are required'),
    industryType: Yup.string().required('Industry type is required'),
    digitalAssetsTrading: Yup.string().required('Digital assets are required'),
    anticipatedMonthlyCryptoTransactionsId: Yup.number().required('Please select transaction volume').nullable(),
    anticipatedMonthlyCryptoVolumeUsdId: Yup.number().required('Please select USD volume').nullable(),
});

const personalValidationSchema = Yup.object({
    purchaseCryptoMonthlyVolume: Yup.number().required('Required').min(0, 'Must be positive'),
    purchaseCryptoAverageAmount: Yup.number().required('Required').min(0, 'Must be positive'),
    purchaseCryptoTotalAmountPerMonth: Yup.number().required('Required').min(0, 'Must be positive'),
    sellingCryptoAverageAmount: Yup.number().required('Required').min(0, 'Must be positive'),
    sellingCryptoMonthlyVolume: Yup.number().required('Required').min(0, 'Must be positive'),
    sellingCryptoTotalAmountPerMonth: Yup.number().required('Required').min(0, 'Must be positive'),
    totalMonthlyVolume: Yup.number().required('Required').min(0, 'Must be positive'),
    totalAverageAmount: Yup.number().required('Required').min(0, 'Must be positive'),
    totalMonthlyAmount: Yup.number().required('Required').min(0, 'Must be positive'),
    btcTradingPercentage: Yup.number().required('Required').min(0, 'Must be 0-100').max(100, 'Must be 0-100'),
    ethTradingPercentage: Yup.number().required('Required').min(0, 'Must be 0-100').max(100, 'Must be 0-100'),
    usdcTradingPercentage: Yup.number().required('Required').min(0, 'Must be 0-100').max(100, 'Must be 0-100'),
    otherCoinsTradingPercentage: Yup.number().required('Required').min(0, 'Must be 0-100').max(100, 'Must be 0-100'),
});

export const UnlockCryptoModal = () => {
    const dispatch = useDispatch();
    const onClose = () => dispatch(closeModal());
    const onSuccess = () => dispatch(getCustomerComponentResources());
    const { userInfo } = useSelector(selectComponentResources);
    const [dropdownOptions, setDropdownOptions] = useState<any>(null);
    const [status, setStatus] = useState<'loading' | 'error' | 'pending' | 'form'>('loading');

    const isBusinessAccount = userInfo?.accountType === 'Business';

    useEffect(() => {
        const init = async () => {
            try {
                const statusRes = await instance.get(endpoints.profilemodule.checkEnablementStatus);

                if (statusRes.data.status !== '1') {
                    setStatus('error');
                    return;
                }

                if (statusRes.data.details?.bPending) {
                    setStatus('pending');
                    return;
                }

                if (isBusinessAccount) {
                    const dropdownRes = await instance.get<ApiResponse<any>>(
                        endpoints.businessVerificationV3Module.getDropdownData
                    );

                    if (dropdownRes.data.status !== '1') {
                        setStatus('error');
                        return;
                    }

                    setDropdownOptions(dropdownRes.data.details);
                }

                setStatus('form');
            } catch (err) {
                console.error('UnlockCryptoModal init error:', err);
                setStatus('error');
            }
        };

        init();
    }, []);

    const businessInitialValues: CryptoEnablementFormValues = {
        blockchainAddresses: '',
        industryType: '',
        digitalAssetsTrading: '',
        anticipatedMonthlyCryptoTransactionsId: null,
        anticipatedMonthlyCryptoVolumeUsdId: null,
        cryptoBusinessPlan: '',
        cryptoPaymentTypes: '',
        integrationMethodId: null,
        expectedMonthlyPaymentVolumeUsdId: null,
        averageTransactionSize: '',
        settlementPreferenceId: null,
    };

    const personalInitialValues: PersonalCryptoFormValues = {
        purchaseCryptoMonthlyVolume: '',
        purchaseCryptoAverageAmount: '',
        purchaseCryptoTotalAmountPerMonth: '',
        sellingCryptoAverageAmount: '',
        sellingCryptoMonthlyVolume: '',
        sellingCryptoTotalAmountPerMonth: '',
        totalMonthlyVolume: '',
        totalAverageAmount: '',
        totalMonthlyAmount: '',
        btcTradingPercentage: '',
        ethTradingPercentage: '',
        usdcTradingPercentage: '',
        otherCoinsTradingPercentage: '',
    };

    const handleBusinessSubmit = async (
        values: CryptoEnablementFormValues,
        { setSubmitting }: FormikHelpers<CryptoEnablementFormValues>
    ) => {
        try {
            const response = await instance.post<ApiResponse<any>>(
                endpoints.profilemodule.requestEnablement,
                {
                    accountType: 'Business',
                    cryptoQuestions: values,
                }
            );

            if (response.data.status === '1') {
                Toast.openSuccessToast('Crypto enablement request submitted successfully! Our compliance team will review your request.');
                onSuccess();
                onClose();
            } else {
                Toast.openErrorToast('Failed to submit request');
            }
        } catch (error: any) {
            console.error('Error submitting crypto enablement:', error);
            Toast.openErrorToast('Failed to submit request');
        } finally {
            setSubmitting(false);
        }
    };

    const handlePersonalSubmit = async (
        values: PersonalCryptoFormValues,
        { setSubmitting }: FormikHelpers<PersonalCryptoFormValues>
    ) => {
        try {
            const response = await instance.post<ApiResponse<any>>(
                endpoints.profilemodule.requestEnablement,
                {
                    accountType: 'Personal',
                    personalCryptoQuestions: {
                        purchaseCryptoMonthlyVolume: parseFloat(values.purchaseCryptoMonthlyVolume),
                        purchaseCryptoAverageAmount: parseFloat(values.purchaseCryptoAverageAmount),
                        purchaseCryptoTotalAmountPerMonth: parseFloat(values.purchaseCryptoTotalAmountPerMonth),
                        sellingCryptoAverageAmount: parseFloat(values.sellingCryptoAverageAmount),
                        sellingCryptoMonthlyVolume: parseFloat(values.sellingCryptoMonthlyVolume),
                        sellingCryptoTotalAmountPerMonth: parseFloat(values.sellingCryptoTotalAmountPerMonth),
                        totalMonthlyVolume: parseFloat(values.totalMonthlyVolume),
                        totalAverageAmount: parseFloat(values.totalAverageAmount),
                        totalMonthlyAmount: parseFloat(values.totalMonthlyAmount),
                        btcTradingPercentage: parseFloat(values.btcTradingPercentage),
                        ethTradingPercentage: parseFloat(values.ethTradingPercentage),
                        usdcTradingPercentage: parseFloat(values.usdcTradingPercentage),
                        otherCoinsTradingPercentage: parseFloat(values.otherCoinsTradingPercentage),
                    },
                }
            );

            if (response.data.status === '1') {
                Toast.openSuccessToast('Crypto enablement request submitted successfully!');
                onSuccess();
                onClose();
            } else {
                Toast.openErrorToast('Failed to submit request');
            }
        } catch (error: any) {
            console.error('Error submitting crypto enablement:', error);
            Toast.openErrorToast('Failed to submit request');
        } finally {
            setSubmitting(false);
        }
    };

    const integrationMethodOptions: SelectOption[] = [
        { label: 'API Integration', value: 1 },
        { label: 'Hosted Payment Page', value: 2 },
        { label: 'E-commerce Plugin', value: 3 },
        { label: 'Other', value: 4 },
    ];

    const settlementPreferenceOptions: SelectOption[] = [
        { label: 'Keep as Crypto', value: 1 },
        { label: 'Convert to Fiat', value: 2 },
        { label: 'Combination of Both', value: 3 },
    ];

    // ─── SHARED: Loading state ────────────────────────────────────────────────
    if (status === 'loading') {
        return (
            <ModalComponent
                title="Enable Crypto Services"
                onCloseModal={onClose}
                className="UnlockCryptoModal"
            >
                <div className="UnlockCryptoLoading">
                    <Spinner />
                </div>
            </ModalComponent>
        );
    }

    // ─── SHARED: Error state ──────────────────────────────────────────────────
    if (status === 'error') {
        return (
            <ModalComponent
                title="Enable Crypto Services"
                onCloseModal={onClose}
                className="UnlockCryptoModal"
            >
                <div className="UnlockCryptoState">
                    <h3>Something went wrong</h3>
                    <p>Unable to load crypto enablement. Please try again later.</p>
                    <Button variety="full" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </ModalComponent>
        );
    }

    // ─── SHARED: Pending state ────────────────────────────────────────────────
    if (status === 'pending') {
        return (
            <ModalComponent
                title="Crypto Enablement Request"
                onCloseModal={onClose}
                className="UnlockCryptoModal"
            >
                <div className="UnlockCryptoState">
                    <h3>Request Under Review</h3>
                    <p>
                        Your crypto enablement request is currently under review by our compliance
                        team. We'll notify you once the review is complete.
                    </p>
                    <Button variety="full" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </ModalComponent>
        );
    }

    // ─── PERSONAL: Form ───────────────────────────────────────────────────────
    if (!isBusinessAccount) {
        return (
            <ModalComponent
                title="Enable Crypto Services"
                onCloseModal={onClose}
                className="UnlockCryptoModal"
            >
                <Formik
                    initialValues={personalInitialValues}
                    validationSchema={personalValidationSchema}
                    onSubmit={handlePersonalSubmit}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <p>Please provide information about your anticipated crypto trading activity.</p>

                            <h3>Purchase Crypto</h3>
                            <FormTextField
                                field="purchaseCryptoMonthlyVolume"
                                label="Monthly Volume *"
                                type="number"
                            />
                            <FormTextField
                                field="purchaseCryptoAverageAmount"
                                label="Average Amount *"
                                type="number"
                                currencySymbol="$"
                            />
                            <FormTextField
                                field="purchaseCryptoTotalAmountPerMonth"
                                label="Total Amount Per Month *"
                                type="number"
                                currencySymbol="$"
                            />

                            <h3>Selling Crypto</h3>
                            <FormTextField
                                field="sellingCryptoMonthlyVolume"
                                label="Monthly Volume *"
                                type="number"
                            />
                            <FormTextField
                                field="sellingCryptoAverageAmount"
                                label="Average Amount *"
                                type="number"
                                currencySymbol="$"
                            />
                            <FormTextField
                                field="sellingCryptoTotalAmountPerMonth"
                                label="Total Amount Per Month *"
                                type="number"
                                currencySymbol="$"
                            />

                            <h3>Total Trading Activity</h3>
                            <FormTextField
                                field="totalMonthlyVolume"
                                label="Total Monthly Volume *"
                                type="number"
                            />
                            <FormTextField
                                field="totalAverageAmount"
                                label="Total Average Amount *"
                                type="number"
                                currencySymbol="$"
                            />
                            <FormTextField
                                field="totalMonthlyAmount"
                                label="Total Monthly Amount *"
                                type="number"
                                currencySymbol="$"
                            />

                            <h3>Trading Percentages (Must total 100%)</h3>
                            <FormTextField
                                field="btcTradingPercentage"
                                label="BTC Trading % *"
                                type="number"
                            />
                            <FormTextField
                                field="ethTradingPercentage"
                                label="ETH Trading % *"
                                type="number"
                            />
                            <FormTextField
                                field="usdcTradingPercentage"
                                label="USDC Trading % *"
                                type="number"
                            />
                            <FormTextField
                                field="otherCoinsTradingPercentage"
                                label="Other Coins % *"
                                type="number"
                            />

                            <div className="FullWidth Buttons">
                                <Button
                                    variety="full"
                                    type="submit"
                                    disabled={isSubmitting}
                                    showSubmittingSpinner={isSubmitting}
                                >
                                    Submit Request
                                </Button>
                                <Button
                                    variety="full"
                                    priority="secondary"
                                    type="button"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </ModalComponent>
        );
    }

    // ─── BUSINESS: Form ───────────────────────────────────────────────────────
    return (
        <ModalComponent
            title="Enable Crypto Services"
            onCloseModal={onClose}
            className="UnlockCryptoModal"
        >
            <Formik
                initialValues={businessInitialValues}
                validationSchema={businessValidationSchema}
                onSubmit={handleBusinessSubmit}
            >
                {({ isSubmitting }) => (
                    <Form>
                        <p>
                            Please provide the following information to enable crypto services for
                            your account.
                        </p>

                        <h3>Crypto OTC Questions</h3>

                        <FormTextField
                            field="blockchainAddresses"
                            label="Blockchain Address(es) for Transactions *"
                            placeholder="Enter blockchain addresses"
                        />
                        <FormTextField
                            field="industryType"
                            label="Industry Type *"
                            placeholder="e.g., DeFi, NFT, Exchange, etc."
                        />
                        <FormTextField
                            field="digitalAssetsTrading"
                            label="Digital Assets You Will Be Trading *"
                            placeholder="e.g., BTC, ETH, USDC, etc."
                        />
                        <WrappedFormSingleSelectField
                            fieldName="anticipatedMonthlyCryptoTransactionsId"
                            options={dropdownOptions?.transactionVolumes ?? []}
                            label="Anticipated Monthly Crypto Transactions *"
                        />
                        <WrappedFormSingleSelectField
                            fieldName="anticipatedMonthlyCryptoVolumeUsdId"
                            options={dropdownOptions?.monthlyUsdEquivalents ?? []}
                            label="Anticipated Monthly Crypto Volume (USD) *"
                        />
                        <FormTextField
                            type="textarea"
                            field="cryptoBusinessPlan"
                            label="Business Plan (Optional)"
                            placeholder="Describe your crypto business plan"
                        />

                        <h3>PORTL Questions (Optional)</h3>

                        <FormTextField
                            field="cryptoPaymentTypes"
                            label="Types of Crypto Payments You Will Accept"
                            placeholder="e.g., BTC, ETH, stablecoins, etc."
                        />
                        <WrappedFormSingleSelectField
                            fieldName="integrationMethodId"
                            options={integrationMethodOptions}
                            label="Integration Method"
                        />
                        <WrappedFormSingleSelectField
                            fieldName="expectedMonthlyPaymentVolumeUsdId"
                            options={dropdownOptions?.monthlyUsdEquivalents ?? []}
                            label="Expected Monthly Payment Volume (USD)"
                        />
                        <FormTextField
                            field="averageTransactionSize"
                            label="Average Transaction Size"
                            placeholder="e.g., 100"
                            type="number"
                        />
                        <WrappedFormSingleSelectField
                            fieldName="settlementPreferenceId"
                            options={settlementPreferenceOptions}
                            label="Settlement Preference"
                        />

                        <div className="FullWidth Buttons">
                            <Button
                                variety="full"
                                type="submit"
                                disabled={isSubmitting}
                                showSubmittingSpinner={isSubmitting}
                            >
                                Submit Request
                            </Button>
                            <Button
                                variety="full"
                                priority="secondary"
                                type="button"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </ModalComponent>
    );
};